import {
  AssociationIDsFilter,
  BooleanFilter,
  DeleteItemGqlQuery,
  DeleteItemGqlStatus,
  FetchItemCacheKey,
  FetchItemGqlQuery,
  FetchItemsCacheKey,
  FetchItemsClearItemsFilters,
  FetchItemsEditItem,
  FetchItemsErrorMessage,
  FetchItemsFetched,
  FetchItemsFilterItems,
  FetchItemsGqlQuery,
  FetchItemsIsPlaceholderData,
  FetchItemsLimit,
  FetchItemsPage,
  FetchItemsPaginateItems,
  FetchItemsPrefetchItems,
  FetchItemsShowItem,
  FetchItemsSortItems,
  FetchItemsTotalCount,
  ID,
  IDFilter,
  IdFilterType,
  ItemGqlError,
  PositiveIdFilterType,
  StringArrayFilter,
  StringFilter,
  TextFilterType,
  UpdateItemCacheKeys,
  UpdateItemGqlQuery,
  UpdateItemGqlStatus
} from '../../../types';

import {
  FetchCategoriesCacheKey,
  FetchCategoriesFilters,
  FetchCategoriesLimit,
  FetchCategoriesPage,
  FetchCategoriesSort,
  FetchFinCategoriesFilters
} from '../../categories/categoriesTypes';
import { LifestyleDetectionID } from '../../lifestyleDetections/lifestyleDetectionsTypes';
import { LifestyleID } from '../../lifestyles/lifestylesTypes';

import {
  ProductCategoryID,
  ProductCategoryName,
  ProductCustomTagName,
  ProductTeamID,
  ProductTeamUUID
} from './productsFields';

interface SimilarProductsImageFilterType {
  imageUrl: string;
  projectId: ID;
  taskId: ID;
}

interface SimilarProductsFilterType {
  productId: ID;
  imageId: ID;
}

interface SimilarProductsProductImageIdFilterType {
  imageId: ID;
  imageUrl: string;
}

export interface SimilarProductsByCustomAreaFilter {
  centerX: number;
  centerY: number;
  detectRequestId: string;
  height: number;
  width: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export interface SimilarProductsByObjectInLifestyleFilter {
  detectionId: LifestyleDetectionID;
}

export interface SimilarProductsByDetectedIdFilter {
  detectionId: LifestyleDetectionID;
  lifestyleId?: LifestyleID;
}

export interface SearchProductsByDetectedIdsFilter {
  detectionIds: string[];
  lifestyleId?: LifestyleID;
}

export interface FetchGroupedByDetectedIdProductsFilters
  extends FetchProductsFilters {
  searchByDetectedIds: SearchProductsByDetectedIdsFilter;
}

export type FetchGroupedByDetectedIdProductsLimit =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10;

export interface DetectedIdProductsGroup<ProductItemType> {
  detectedId: string;
  products: ProductItemType[];
}

// The multi-ID filters (collectionIds, materialIds, …) are `IdFilter` input
// objects on the `cache` backend but still bare `[ID!]` lists on the `api`
// backend, so they accept both shapes; pick per the backend being queried.
export interface FetchProductsFilters {
  and?: FetchProductsFilters[];
  or?: FetchProductsFilters[];
  id?: IdFilterType;
  name?: TextFilterType;
  brandId?: IdFilterType;
  productCategoryId?: IdFilterType;
  productParentCategoryIds?: AssociationIDsFilter;
  styleId?: IdFilterType;
  taskId?: IdFilterType;
  userId?: IdFilterType;
  collectionIds?: AssociationIDsFilter | IdFilterType;
  materialIds?: AssociationIDsFilter | IdFilterType;
  colorIds?: AssociationIDsFilter | IdFilterType;
  tagIds?: AssociationIDsFilter | IdFilterType;
  customTagIds?: IdFilterType;
  statusIds?: AssociationIDsFilter | IdFilterType;
  mistakeTypeIds?: AssociationIDsFilter | IdFilterType;
  fileVersionTagIds?: AssociationIDsFilter | IdFilterType;
  favorite?: BooleanFilter;
  currentTeamFavorite?: BooleanFilter;
  nda?: BooleanFilter;
  blocked?: BooleanFilter;
  clientIds?: AssociationIDsFilter | IdFilterType;
  nameSkuTags?: StringFilter;
  sku?: TextFilterType;
  lifestyleIds?: AssociationIDsFilter | IdFilterType;
  associatedMaterialIds?: IdFilterType;
  previewModelId?: IdFilterType;
  productTypeId?: IdFilterType;
  seasonalId?: IdFilterType;
  renderTypeIds?: AssociationIDsFilter | IdFilterType;
  similarImage?: SimilarProductsImageFilterType;
  similarProduct?: SimilarProductsFilterType;
  similarProductImageId?: SimilarProductsProductImageIdFilterType;
  aiFeatureTags?: TextFilterType;
  aiInteriorStyleTags?: TextFilterType;
  aiMainColorTags?: TextFilterType;
  aiMaterialTags?: TextFilterType;
  aiRoomClassificationTags?: TextFilterType;
  withTaskName?: BooleanFilter;
  collectionId?: PositiveIdFilterType;
  teamIds?: ProductTeamUUID[];
  teamIdFavorite?: ProductTeamID;
  similarProductsByCustomArea?: SimilarProductsByCustomAreaFilter;
  similarProductsByObjectInLifestyle?: SimilarProductsByObjectInLifestyleFilter;
  searchByDetectedId?: SimilarProductsByDetectedIdFilter;
  searchByDetectedIds?: SearchProductsByDetectedIdsFilter;
}

export interface FetchProductsElasticSearchFilters {
  search?: StringFilter;
  productCategoryId?: IDFilter;
  customTagNames?: StringArrayFilter;
}

export type FetchProductsFiltersToPersist = (keyof FetchProductsFilters)[];

export enum FetchProductsSortableFields {
  ID = 'id',
  NAME = 'name',
  CREATED_AT = 'createdAt'
}

export enum FetchProductsFilterFields {
  ID = 'id',
  NAME = 'name',
  NAME_SKU_TAGS = 'nameSkuTags',
  BRAND_ID = 'brandId',
  PRODUCT_CATEGORY_ID = 'productCategoryId',
  PRODUCT_PARENT_CATEGORY_IDS = 'productParentCategoryIds',
  STYLE_ID = 'styleId',
  MATERIAL_IDS = 'materialIds',
  COLOR_IDS = 'colorIds',
  BLOCKED = 'blocked',
  FAVORITE = 'favorite',
  CURRENT_TEAM_FAVORITE = 'currentTeamFavorite',
  NDA = 'nda',
  CREATED_AT = 'createdAt',
  CLIENT_IDS = 'clientIds',
  PRODUCT_TYPE_ID = 'productTypeId',
  SEASONAL_ID = 'seasonalId',
  SIMILAR_IMAGE = 'similarImage',
  SIMILAR_PRODUCT = 'similarProduct',
  RENDER_TYPE_IDS = 'renderTypeIds',
  AI_FEATURE_TAGS = 'aiFeatureTags',
  AI_INTERIOR_STYLE_TAGS = 'aiInteriorStyleTags',
  AI_MAIN_COLOR_TAGS = 'aiMainColorTags',
  AI_MATERIAL_TAGS = 'aiMaterialTags',
  AI_ROOM_CLASSIFICATION_TAGS = 'aiRoomClassificationTags',
  WITH_TASK_NAME = 'withTaskName',
  COLLECTION_ID = 'collectionId',
  TEAM_IDS = 'teamIds',
  TEAM_ID_FAVORITE = 'teamIdFavorite',
  SIMILAR_PRODUCTS_BY_CUSTOM_AREA = 'similarProductsByCustomArea',
  SIMILAR_PRODUCTS_BY_OBJECT_IN_LIFESTYLE = 'similarProductsByObjectInLifestyle',
  SEARCH_BY_DETECTED_ID = 'searchByDetectedId',
  SEARCH_BY_DETECTED_IDS = 'searchByDetectedIds'
}

export enum FetchProductsElasticSearchFilterFields {
  SEARCH = 'search',
  CUSTOM_TAG_NAMES = 'customTagNames',
  PRODUCT_CATEGORY_ID = 'productCategoryId'
}

export enum FetchProductsSortTypes {
  ID_ASC = 'ID_ASC',
  ID_DESC = 'ID_DESC',
  NAME_ASC = 'NAME_ASC',
  NAME_DESC = 'NAME_DESC',
  CREATED_AT_ASC = 'CREATED_AT_ASC',
  CREATED_AT_DESC = 'CREATED_AT_DESC',
  SELECTED_PRODUCTS_COUNT_DESC = 'SELECTED_PRODUCTS_COUNT_DESC',
  USERS_CHOSEN_PRODUCTS_COUNT_DESC = 'USERS_CHOSEN_PRODUCTS_COUNT_DESC',
  SCORE_ASC = 'SCORE_ASC',
  SCORE_DESC = 'SCORE_DESC'
}

export type FetchProductsGqlQuery = FetchItemsGqlQuery;
export type FetchProductGqlQuery = FetchItemGqlQuery;

export type FetchProductCacheKey = FetchItemCacheKey;
export type FetchProductsCacheKey = FetchItemsCacheKey;
export type FetchProductsCacheKeys = FetchProductsCacheKey[];
export type FetchProductCategoriesCacheKey = FetchCategoriesCacheKey;

export type FetchProductsFetched = FetchItemsFetched;
export type FetchProductsErrorMessage = FetchItemsErrorMessage;
export type FetchProductsIsPlaceholderData = FetchItemsIsPlaceholderData;
export type FetchProductsPage = FetchItemsPage;
export type FetchProductsLimit = FetchItemsLimit;
export type FetchProductsSort = FetchProductsSortTypes[];
export type FetchProductsTotalCount = FetchItemsTotalCount;
export type FetchProductsFilterProducts =
  FetchItemsFilterItems<FetchProductsFilters>;
export type FetchProductsSortProducts = FetchItemsSortItems;
export type FetchProductsPaginateProducts = FetchItemsPaginateItems;
export type FetchProductsPrefetchProducts = FetchItemsPrefetchItems;
export type FetchProductsClearProductsFilters = FetchItemsClearItemsFilters;
export type FetchProductsShowProduct = FetchItemsShowItem;
export type FetchProductsEditProduct = FetchItemsEditItem;

export type FetchProductsElasticSearchFilterProducts =
  FetchItemsFilterItems<FetchProductsElasticSearchFilters>;

export type FetchProductCategoriesFilters = FetchCategoriesFilters;
export type FetchFinProductCategoriesFilters = FetchFinCategoriesFilters;
export type FetchProductCategoriesLimit = FetchCategoriesLimit;
export type FetchProductCategoriesSort = FetchCategoriesSort;
export type FetchProductCategoriesPage = FetchCategoriesPage;

export type DeleteProductGqlQuery = DeleteItemGqlQuery;
export type DeleteProductGqlStatus = DeleteItemGqlStatus;
export type ToggleProductBlockedGqlQuery = UpdateItemGqlQuery;
export type ToggleProductBlockedCacheKeys = UpdateItemCacheKeys;
export type ToggleProductBlockedGqlStatus = UpdateItemGqlStatus;
export type IndexProductCustomTagsGqlQuery = UpdateItemGqlQuery;
export type IndexProductCustomTagsCacheKeys = UpdateItemCacheKeys;
export type IndexProductCustomTagsGqlStatus = UpdateItemGqlStatus;
export type ProductGqlError = ItemGqlError;

export interface FetchProductsFiltersFilters {
  scope?: [FetchProductsFilterFields];
}

export interface FetchElasticSearchProductsMeta {
  categories: Record<ProductCategoryName, ProductCategoryID>[];
  tags: [ProductCustomTagName, number][];
}
